/**
 * "Replace with another plan…" — Movatic admins only.
 *
 * Three steps over ant's `replaceMembership`:
 *  1. Pick plan B: a plan in the same system with no archive date. ant is
 *     asked for those only, by name, at most PLAN_PAGE (ant's page size) at a
 *     time; the search goes to ant, so a plan past the first page is found.
 *  2. Map prices: one row per old price a moving rider uses. Which prices need
 *     a row comes from ant itself: picking B runs a dry run with no mapping,
 *     and ant answers either with the review (nothing to map, e.g. a free
 *     plan, so this step is skipped) or `unmapped_price` with those price ids.
 *     That includes free prices, since nobody may be left on an archived price.
 *  3. Review: a dry run with the mapping, shown in plain words, refusals
 *     included. Typing the old plan's name runs it for real.
 *
 * v1 defaults (Julio did not answer these; they match the ant side): Movatic
 * admins only, canceled-but-paid riders whose term has not ended move, usage
 * carries over, no rider notification.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';

import {
  useReplaceMembershipMutation,
  type MembershipReplaceResult,
  type ReplaceMembershipError,
  type ReplaceMembershipInput,
  type PriceMapping,
} from '../../../hooks/membership/mutation/V2/useReplaceMembershipMutation';
import {
  useMembershipsRelayQuery,
  type MembershipGraphQL,
  type MembershipPriceGraphQL,
} from '../../../hooks/membership/query/V2/useMembershipsRelay';
import CustomToast from '../../../components/Redesing/custom-toast';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import {
  priceIncompatibility,
  priceLabel,
  refusalText,
  reviewLines,
  successMessage,
} from './replace-membership-copy';

const STEPS = ['Pick the new plan', 'Map prices', 'Review'];
/** ant's Membership read refuses a page over 100. */
export const PLAN_PAGE = 100;
const SEARCH_DELAY_MS = 300;
const PLAN_SORT = [{ field: 'NAME' as const, order: 'ASC' as const }];

interface ReplaceMembershipModalProps {
  open: boolean;
  onClose: () => void;
  /** Plan A, the one being replaced. */
  membership: MembershipGraphQL;
  /** The query can retain another plan while this route's membership loads. */
  routeMembershipId: string;
  /** Called after the real run succeeded. */
  onReplaced: (result: MembershipReplaceResult) => void;
}

interface Review {
  input: Pick<
    ReplaceMembershipInput,
    'membershipId' | 'replacementMembershipId' | 'priceMap'
  >;
  result: MembershipReplaceResult | null;
  errors: ReplaceMembershipError[];
}

function priceIdsFrom(error?: ReplaceMembershipError): string[] {
  const ids = error?.vars?.price_ids;
  return Array.isArray(ids) ? ids.map(String) : [];
}

function ReplaceMembershipModal({
  open,
  onClose,
  membership,
  routeMembershipId,
  onReplaced,
}: ReplaceMembershipModalProps) {
  const isCurrentMembership = membership.id === routeMembershipId;
  const [step, setStep] = useState(0);
  const [search, setSearch] = useState('');
  // The picked plan itself, not just its id: a later search can drop it from
  // the list, and the price step still needs its prices.
  const [selected, setSelected] = useState<MembershipGraphQL | null>(null);
  const selectedId = selected?.id ?? null;
  const [pickError, setPickError] = useState<ReplaceMembershipError[]>([]);
  const [requiredPriceIds, setRequiredPriceIds] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [review, setReview] = useState<Review | null>(null);
  const [runErrors, setRunErrors] = useState<ReplaceMembershipError[]>([]);
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const requestToken = useRef(0);
  const currentModal = useRef({ open, membershipId: membership.id, routeMembershipId });
  currentModal.current = { open, membershipId: membership.id, routeMembershipId };

  const { replaceMembership } = useReplaceMembershipMutation();

  // The input shows the picked plan's name; that is not a search, so reopening
  // the dropdown still lists every plan.
  const query = selected && search === selected.name ? '' : search;
  const searchTerm = (useSearchDebounce(query, SEARCH_DELAY_MS) ?? '').trim();
  const {
    data: plans,
    isLoading: plansLoading,
    error: plansError,
    hasMore: morePlans,
    refetch: refetchPlans,
  } = useMembershipsRelayQuery(
    {
      systemId: membership.systemId ?? undefined,
      hasArchiveDate: false,
      name: searchTerm || undefined,
    },
    {
      enabled: open && isCurrentMembership && Boolean(membership.systemId),
      limit: PLAN_PAGE,
      sort: PLAN_SORT,
    }
  );

  // Reset on navigation even while the query retains the previous source.
  // Closing, navigating or unmounting also invalidates pending responses.
  useEffect(() => {
    setStep(0);
    setSearch('');
    setSelected(null);
    setPickError([]);
    setRequiredPriceIds([]);
    setMapping({});
    setReview(null);
    setRunErrors([]);
    setReason('');
    setConfirmText('');
    setBusy(false);
    return () => {
      requestToken.current += 1;
    };
  }, [open, membership.id, routeMembershipId]);

  const candidates = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (plans ?? []).filter(
      (plan) =>
        plan.id !== membership.id &&
        !plan.archivedOn &&
        (!term || plan.name.toLowerCase().includes(term))
    );
  }, [plans, query, membership.id]);

  const names = { oldName: membership.name, newName: selected?.name ?? 'the new plan' };
  const oldPricesById = useMemo(
    () => new Map(membership.prices.map((price) => [price.id, price])),
    [membership.prices]
  );

  const priceMap = (): PriceMapping[] =>
    requiredPriceIds
      .filter((id) => mapping[id])
      .map((id) => ({ fromPriceId: id, toPriceId: mapping[id] }));

  const inputsLocked = !isCurrentMembership || busy || review !== null;
  const allMapped = requiredPriceIds.every((id) => Boolean(mapping[id]));
  const normalizeName = (name: string) =>
    name.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  const confirmMatches = normalizeName(confirmText) === normalizeName(membership.name);
  const confirmMismatch = confirmText.trim() !== '' && !confirmMatches;
  const reviewOk =
    isCurrentMembership &&
    review?.input.membershipId === membership.id &&
    Boolean(review?.result) &&
    (review?.errors.length ?? 0) === 0;
  const alreadyReplaced = review?.result?.status === 'already_replaced';

  const invalidateReview = () => {
    setReview(null);
    setRunErrors([]);
    setConfirmText('');
  };

  const isCurrentRequest = (token: number, sourceId: string) =>
    token === requestToken.current &&
    currentModal.current.open &&
    sourceId === currentModal.current.membershipId &&
    sourceId === currentModal.current.routeMembershipId;

  const runDry = (input: Review['input']) =>
    replaceMembership({ ...input, dryRun: true });

  const selectPlan = (plan: MembershipGraphQL | null) => {
    if (inputsLocked || (plan?.id ?? null) === selectedId) return;
    setSelected(plan);
    setPickError([]);
    setRequiredPriceIds([]);
    setMapping({});
    setReason('');
    invalidateReview();
  };

  const selectPrice = (fromId: string, toId: string) => {
    if (inputsLocked) return;
    setMapping({ ...mapping, [fromId]: toId });
    invalidateReview();
  };

  // Step 1 -> ask ant what needs mapping.
  const handlePickNext = async () => {
    if (!selectedId || inputsLocked) return;
    const input: Review['input'] = {
      membershipId: membership.id,
      replacementMembershipId: selectedId,
      priceMap: [],
    };
    const token = ++requestToken.current;
    setBusy(true);
    setPickError([]);
    const outcome = await runDry(input);
    if (!isCurrentRequest(token, input.membershipId)) return;
    setBusy(false);
    if (outcome.isSuccess) {
      setRequiredPriceIds([]);
      setReview({ input, result: outcome.data, errors: [] });
      setStep(2);
      return;
    }
    const unmapped = outcome.errors.find((error) => error.code === 'unmapped_price');
    if (unmapped) {
      setRequiredPriceIds(priceIdsFrom(unmapped));
      setStep(1);
      return;
    }
    setPickError(outcome.errors);
  };

  // Step 2 -> dry run with the mapping.
  const handleMapNext = async () => {
    if (!selectedId || inputsLocked || !allMapped) return;
    const input: Review['input'] = {
      membershipId: membership.id,
      replacementMembershipId: selectedId,
      priceMap: priceMap(),
    };
    const token = ++requestToken.current;
    setBusy(true);
    const outcome = await runDry(input);
    if (!isCurrentRequest(token, input.membershipId)) return;
    setBusy(false);
    setReview({
      input,
      result: outcome.isSuccess ? outcome.data : null,
      errors: outcome.errors,
    });
    setRunErrors([]);
    setStep(2);
  };

  const handleConfirm = async () => {
    if (busy || !review || !reviewOk || alreadyReplaced || !confirmMatches) return;
    const token = ++requestToken.current;
    setBusy(true);
    setRunErrors([]);
    const outcome = await replaceMembership({
      // Plan ids and mappings must be the exact inputs ant reviewed. The
      // optional reason is audit text entered on the review step.
      ...review.input,
      dryRun: false,
      reason: reason.trim() || undefined,
    });
    if (!isCurrentRequest(token, review.input.membershipId)) return;
    setBusy(false);
    if (!outcome.isSuccess || !outcome.data) {
      setRunErrors(outcome.errors);
      return;
    }
    CustomToast({
      type: 'success',
      message: successMessage(outcome.data, names),
      duration: 6000,
    });
    onReplaced(outcome.data);
    onClose();
  };

  const handleBack = () => {
    if (busy) return;
    // Editing again requires a fresh dry run, even if the choices stay the same.
    invalidateReview();
    setStep(step === 2 && requiredPriceIds.length > 0 ? 1 : 0);
  };

  const renderErrors = (errors: ReplaceMembershipError[]) =>
    errors.map((error, index) => (
      <Alert key={`${error.code}-${index}`} severity="error" data-id="replace-refusal">
        {refusalText(error, names)}
      </Alert>
    ));

  const planPrices = (plan: MembershipGraphQL) => {
    const live = plan.prices.filter((price) => !price.archivedOn);
    return live.length === 0
      ? 'Free'
      : live.map((price) => priceLabel(price)).join(' | ');
  };

  const renderPick = () => (
    <Stack spacing={2}>
      <Typography variant="body2">
        Every rider on &quot;{membership.name}&quot; moves to the plan you pick, keeping
        their renewal date, usage and pending verification. Then &quot;{membership.name}
        &quot; is archived. Only plans in the same system with no archive date are listed.
      </Typography>
      {plansError ? (
        <Alert
          severity="error"
          data-id="replace-plans-error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={refetchPlans}
              sx={{ float: 'none' }}
            >
              Retry
            </Button>
          }
        >
          Could not load the plans in this system. Try again.
        </Alert>
      ) : (
        <Autocomplete
          disabled={inputsLocked}
          options={candidates}
          value={selected}
          inputValue={search}
          onInputChange={(_event, value) => {
            if (!inputsLocked) setSearch(value);
          }}
          onChange={(_event, plan) => selectPlan(plan)}
          // ant filters by name; candidates drops this plan and archived ones.
          filterOptions={(options) => options}
          getOptionLabel={(plan) => plan.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={plansLoading}
          loadingText="Loading memberships…"
          // Always open below the field (no flip above it), with a short list
          // that scrolls, so it reads as part of the step.
          componentsProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [{ name: 'flip', enabled: false }],
            },
          }}
          ListboxProps={{ style: { maxHeight: 240 } }}
          noOptionsText={
            query.trim()
              ? `No membership in this system matches "${query.trim()}".`
              : 'No other membership in this system can take these riders. Create the new one first.'
          }
          renderOption={(props, plan) => (
            <li {...props} key={plan.id}>
              <ListItemText primary={plan.name} secondary={planPrices(plan)} />
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Search membership" size="small" />
          )}
        />
      )}
      {!plansError && !plansLoading && morePlans && (
        <Typography variant="caption" color="text.secondary">
          Showing the first {PLAN_PAGE} plans by name. Search to find others.
        </Typography>
      )}
      {renderErrors(pickError)}
    </Stack>
  );

  const renderMappingRow = (fromId: string) => {
    const from = oldPricesById.get(fromId);
    const options = selected?.prices ?? [];
    const label = from ? priceLabel(from) : `Price ${fromId}`;
    const usable = from
      ? options.filter((to) => priceIncompatibility(from, to) === null)
      : options.filter((to) => !to.archivedOn);
    return (
      <Stack key={fromId} spacing={0.5}>
        <TextField
          select
          disabled={inputsLocked}
          size="small"
          fullWidth
          label={label}
          value={mapping[fromId] ?? ''}
          onChange={(event) => selectPrice(fromId, event.target.value)}
          SelectProps={{ SelectDisplayProps: { 'aria-label': `New price for ${label}` } }}
        >
          {options.map((to: MembershipPriceGraphQL) => {
            const why = from
              ? priceIncompatibility(from, to)
              : to.archivedOn
              ? 'Archived.'
              : null;
            return (
              <MenuItem key={to.id} value={to.id} disabled={Boolean(why)}>
                <ListItemText primary={priceLabel(to)} secondary={why} />
              </MenuItem>
            );
          })}
        </TextField>
        {usable.length === 0 && (
          <Typography variant="caption" color="error">
            No price on &quot;{names.newName}&quot; renews the same way at the same or a
            lower price. Add one to it first.
          </Typography>
        )}
      </Stack>
    );
  };

  const renderMap = () => (
    <Stack spacing={2}>
      <Typography variant="body2">
        Riders on &quot;{membership.name}&quot; use these prices. Pick the price on &quot;
        {names.newName}&quot; each one moves to. Only prices that renew the same way and
        cost the same or less can be picked; the next charge stays on the same date at the
        new price.
      </Typography>
      {requiredPriceIds.map(renderMappingRow)}
    </Stack>
  );

  const renderReview = () => {
    if (!isCurrentMembership || !review || review.input.membershipId !== membership.id)
      return null;
    if (!reviewOk) {
      return (
        <Stack spacing={2}>
          <Typography variant="body2">The plan cannot be replaced as set up:</Typography>
          {renderErrors(review.errors)}
        </Stack>
      );
    }
    const result = review.result as MembershipReplaceResult;
    if (alreadyReplaced) {
      return (
        <Alert severity="info">
          &quot;{membership.name}&quot; was already replaced by &quot;{names.newName}
          &quot;. Nothing to do.
        </Alert>
      );
    }
    return (
      <Stack spacing={2}>
        <Typography variant="subtitle2">
          Nothing has changed yet. This is what will happen:
        </Typography>
        <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }} data-id="replace-review">
          {reviewLines(result, names).map((line) => (
            <Typography
              component="li"
              variant="body2"
              key={line.key}
              color={line.tone === 'warning' ? 'warning.main' : undefined}
            >
              {line.text}
            </Typography>
          ))}
        </Stack>
        <TextField
          label="Reason (optional)"
          disabled={busy}
          size="small"
          fullWidth
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          helperText="Saved in each rider's plan history."
          inputProps={{ maxLength: 500 }}
        />
        <TextField
          label={`Type "${membership.name}" to confirm`}
          disabled={busy}
          size="small"
          fullWidth
          value={confirmText}
          onChange={(event) => setConfirmText(event.target.value)}
          error={confirmMismatch}
          helperText={
            confirmMatches
              ? 'Name matches. You can replace the plan.'
              : confirmMismatch
              ? `Doesn't match "${membership.name}" yet.`
              : 'Replace plan turns on when the name matches.'
          }
        />
        {renderErrors(runErrors)}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isCurrentMembership
          ? `Replace "${membership.name}" with another plan`
          : 'Replace membership'}
      </DialogTitle>
      <DialogContent dividers>
        {isCurrentMembership ? (
          <>
            <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {step === 0 && renderPick()}
            {step === 1 && renderMap()}
            {step === 2 && renderReview()}
          </>
        ) : (
          <Alert severity="info">
            The selected membership must finish loading before it can be replaced.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          spacing={1}
          sx={{ width: '100%', justifyContent: 'flex-end' }}
        >
          {busy && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
          <Button
            color="secondary"
            onClick={onClose}
            disabled={busy}
            sx={{ float: 'none' }}
          >
            Cancel
          </Button>
          {step > 0 && (
            <Button onClick={handleBack} disabled={busy} sx={{ float: 'none' }}>
              Back
            </Button>
          )}
          {step === 0 && (
            <Button
              variant="contained"
              onClick={handlePickNext}
              disabled={inputsLocked || !selectedId}
              sx={{ float: 'none' }}
            >
              Next
            </Button>
          )}
          {step === 1 && (
            <Button
              variant="contained"
              onClick={handleMapNext}
              disabled={inputsLocked || !allMapped}
              sx={{ float: 'none' }}
            >
              Review
            </Button>
          )}
          {step === 2 && reviewOk && !alreadyReplaced && (
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirm}
              disabled={busy || !confirmMatches}
              sx={{ float: 'none' }}
            >
              Replace plan
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default ReplaceMembershipModal;
